/**
 * Event Segment Selector Component
 * Allows associating segments with events for attendee management
 */

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { supabase } from "../../services/supabase";
import { fetchAllRows, pageByNameThenId } from "../../utils/fetchAllRows";
import { QUERY_KEYS } from "../../utils/constants";
import { preventEnterSubmit } from "../../utils/preventEnterSubmit";

export interface EventSegmentSelection {
  segment_id: string;
  segment_name: string;
  association_type: "proposed" | "invited" | "registered" | "attended";
}

interface EventSegmentSelectorProps {
  selected: EventSegmentSelection[];
  onChange: (selections: EventSegmentSelection[]) => void;
  /** Lists that must not be offered (e.g. an Event in a Box audience already linked by the launch). */
  excludeIds?: string[];
}

const EventSegmentSelector: React.FC<EventSegmentSelectorProps> = ({
  selected,
  onChange,
  excludeIds = [],
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] =
    useState<EventSegmentSelection["association_type"]>("proposed");

  // Fetch all segments (from lists table)
  const { data: segments, isError } = useQuery({
    queryKey: [QUERY_KEYS.SEGMENTS, "for-events"],
    queryFn: async () => {
      // Paged: PROD has more segments than PostgREST's 1000-row cap.
      try {
        return await fetchAllRows<any>((from, to) =>
          pageByNameThenId(supabase.from("lists").select("*"), from, to),
        );
      } catch (error) {
        console.warn("Error fetching segments:", error);
        return []; // Return empty array on error instead of crashing
      }
    },
    retry: false, // Don't retry on failure
  });

  const filteredSegments =
    segments?.filter(
      (segment) =>
        segment.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !selected.some((s) => s.segment_id === segment.id) &&
        !excludeIds.includes(segment.id),
    ) || [];

  const handleAddSegment = (segment: { id: string; name: string }) => {
    onChange([
      ...selected,
      {
        segment_id: segment.id,
        segment_name: segment.name,
        association_type: selectedType,
      },
    ]);
    setSearchTerm("");
  };

  const handleRemove = (segmentId: string) => {
    onChange(selected.filter((s) => s.segment_id !== segmentId));
  };

  const handleTypeChange = (
    segmentId: string,
    newType: EventSegmentSelection["association_type"],
  ) => {
    onChange(
      selected.map((s) =>
        s.segment_id === segmentId ? { ...s, association_type: newType } : s,
      ),
    );
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "proposed":
        return "bg-gray-100 text-gray-800";
      case "invited":
        return "bg-blue-100 text-blue-800";
      case "registered":
        return "bg-green-100 text-green-800";
      case "attended":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="space-y-4">
      {/* Selected Segments */}
      {selected.length > 0 && (
        <div className="space-y-2">
          {selected.map((item) => (
            <div
              key={item.segment_id}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className="flex items-center gap-3 flex-1">
                <span className="font-medium text-gray-900">
                  {item.segment_name}
                </span>
                <select
                  value={item.association_type}
                  onChange={(e) =>
                    handleTypeChange(
                      item.segment_id,
                      e.target
                        .value as EventSegmentSelection["association_type"],
                    )
                  }
                  className={`text-xs px-2 py-1 rounded-full border-0 font-medium ${getTypeColor(item.association_type)}`}
                >
                  <option value="proposed">Proposed</option>
                  <option value="invited">Invited</option>
                  <option value="registered">Registered</option>
                  <option value="attended">Attended</option>
                </select>
              </div>
              <button
                type="button"
                onClick={() => handleRemove(item.segment_id)}
                className="p-1 text-red-600 hover:text-red-800"
                title="Remove"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add New Segment */}
      <div className="border border-gray-300 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              onKeyDown={preventEnterSubmit}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search segments..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <select
            value={selectedType}
            onChange={(e) =>
              setSelectedType(
                e.target.value as EventSegmentSelection["association_type"],
              )
            }
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="proposed">Proposed</option>
            <option value="invited">Invited</option>
            <option value="registered">Registered</option>
            <option value="attended">Attended</option>
          </select>
        </div>

        {/* Search Results */}
        {searchTerm && (
          <div className="max-h-60 overflow-y-auto space-y-1">
            {filteredSegments.length > 0 ? (
              filteredSegments.map((segment) => (
                <button
                  key={segment.id}
                  type="button"
                  onClick={() => handleAddSegment(segment)}
                  className="w-full text-left p-3 hover:bg-gray-50 rounded-md border border-transparent hover:border-gray-200 transition-colors"
                >
                  <div className="font-medium text-gray-900">
                    {segment.name}
                  </div>
                  {segment.description && (
                    <div className="text-sm text-gray-500 truncate">
                      {segment.description}
                    </div>
                  )}
                  <div className="text-xs text-gray-400 mt-1">
                    {segment.contact_count || 0} contacts
                    {segment.is_dynamic ? " \u2022 Dynamic" : " \u2022 Static"}
                  </div>
                </button>
              ))
            ) : (
              <div className="text-center py-4 text-gray-500">
                No segments found
              </div>
            )}
          </div>
        )}
      </div>

      {isError && (
        <p className="text-sm text-amber-600 text-center py-2">
          Unable to load segments. You can still create the event without
          segment associations.
        </p>
      )}

      {selected.length === 0 && !searchTerm && !isError && (
        <p className="text-sm text-gray-500 text-center py-4">
          Search and add segments to associate with this event
        </p>
      )}
    </div>
  );
};

export default EventSegmentSelector;
